import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Message, SearchFilters } from '../../../../interfaces/chat';
import { mapAgentFilters } from '../../../../helpers/agentFilterMapper';
import CardChatIA from '../../../CardChatIA/CardChatIA';
import Icon from '@mdi/react';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import styles from './chatMessage.module.scss';

interface ChatMessageProps {
    message: Message;
    user?: any;
    onClose: () => void;
    onRefresh: () => void;
    isLastMessage?: boolean;
    disabled?: boolean;
}

const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

const filterLabelMap: Record<string, string> = {
    tipo: 'Tipo',
    uso: 'Uso',
    marca: 'Marca',
    modelo: 'Modelo',
    version: 'Versión',
    anno: 'Año',
    color: 'Color',
    transmision: 'Transmisión',
    carroceria: 'Carrocería',
    combustible: 'Combustible',
    region: 'Región',
    precio: 'Precio',
    kilometraje: 'Kilometraje',
    tipoVenta: 'Tipo venta',
    concesionario: 'Concesionario',
};

const filterKeyToMappedKey: Record<string, string> = {
    tipo: 'vehicleCategory',
    uso: 'vehicleType',
    marca: 'brand',
    modelo: 'model',
    anno: 'year',
    color: 'color',
    transmision: 'transmission',
    carroceria: 'type',
    combustible: 'fuel',
    region: 'region',
    tipoVenta: 'seller',
    concesionario: 'dealership',
    kilometraje: 'mileage',
};

const getNormalizedFilters = (filters: SearchFilters) => {
    return mapAgentFilters({
        tipo: filters.tipo,
        uso: filters.uso,
        tipoVenta: filters.tipoVenta,
        concesionario: filters.concesionario,
        marca: filters.marca,
        modelo: filters.modelo,
        año: filters.anno,
        color: filters.color,
        transmision: filters.transmision,
        carroceria: filters.carroceria,
        combustible: filters.combustible,
        region: filters.region,
        precio: filters.precio,
        kilometraje: filters.kilometraje,
    }, [], []);
};

const getFilterDisplayValue = (key: string, filters: SearchFilters, mapped: any): string | null => {
    if (key === 'precio') {
        const value = filters.precio;
        if (!value || (value.desde == null && value.hasta == null)) return null;
        const parts = [];
        if (value.desde != null) parts.push(`desde $${value.desde.toLocaleString('es-CL')}`);
        if (value.hasta != null) parts.push(`hasta $${value.hasta.toLocaleString('es-CL')}`);
        return parts.join(' ');
    }

    const mappedKey = filterKeyToMappedKey[key];
    if (mappedKey) {
        const mappedValue = mapped[mappedKey];
        if (!mappedValue) return null;
        if (mappedKey === 'vehicleType') return mappedValue === 'new' ? 'Nuevos' : 'Usados';
        if (Array.isArray(mappedValue)) {
            const display = mappedValue.map((v: any) => typeof v === 'object' ? (v.name || v.value || '') : v).filter(Boolean);
            return display.length > 0 ? display.join(', ') : null;
        }
        return String(mappedValue);
    }

    const value = (filters as any)[key];
    if (Array.isArray(value) && value.length > 0) return value.join(', ');
    return null;
};

// Set a nivel de módulo para rastrear qué mensajes ya se auto-aplicaron.
// Persiste entre mount/unmount del componente (abrir/cerrar chat).
const autoTriggeredMessageIds = new Set<string>();

const ChatMessage: React.FC<ChatMessageProps> = ({ message, user, onClose, onRefresh, isLastMessage, disabled }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isUser = message.role === 'user';
    const isSearch = message.responseType === 'search';
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);
    
    const renderSearchFilters = () => {
        if (!isSearch || !message.filters || !message.filtersApplied?.length) return null;

        const mapped = getNormalizedFilters(message.filters);

        return (
            <div className={styles.searchFilters}>
                <div className={styles.filtersLabel}>Filtros aplicados:</div>
                <div className={styles.filterChips}>
                    {message.filtersApplied.map((key) => {
                        const displayValue = getFilterDisplayValue(key, message.filters!, mapped);
                        if (!displayValue) return null;
                        return (
                            <span key={key} className={styles.filterChip}>
                                {filterLabelMap[key] || key}: {displayValue}
                            </span>
                        );
                    })}
                </div>
            </div>
        );
    };

    const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const link = target.closest('a');
        
        if (link && link.href) {
            e.preventDefault();
            const href = link.getAttribute('href');
            
            if (href) {
                // Check if it's an internal link
                if (href.includes('linzecar.cl')) {
                    // Extract the path and navigate internally
                    const url = new URL(href);
                    const path = url.pathname + url.search + url.hash;
                    navigate(path);
                } else {
                    // External link, open in new tab
                    window.open(href, '_blank');
                }
            }
        }
    };

    const handleViewMore = () => {
        if (!message.filters) {
            navigate('/buscar');
            return;
        }
        
        const filters = message.filters;
                
        // Usar mapAgentFilters para normalizar todos los filtros
        // Esto asegura que los valores coincidan exactamente con lo que espera Filters.tsx
        const agentFilters: any = {
            tipo: filters.tipo,
            uso: filters.uso,
            tipoVenta: filters.tipoVenta,
            concesionario: filters.concesionario,
            marca: filters.marca,
            modelo: filters.modelo,
            año: filters.anno,
            color: filters.color,
            transmision: filters.transmision,
            carroceria: filters.carroceria,
            combustible: filters.combustible,
            region: filters.region,
            precio: filters.precio,
            kilometraje: filters.kilometraje,
        };
        
        // mapAgentFilters normaliza valores contra las constantes reales (COLORS, FUEL_TYPES, etc.)
        // Pasamos arrays vacíos para colors/fuels - el mapper usa constantes como fallback
        const mapped = mapAgentFilters(agentFilters, [], []);
        
        
        // Determinar la ruta base según los filtros mapeados
        const isComercial = mapped.vehicleCategory?.includes('Comerciales');
        const isNew = mapped.vehicleType === 'new';
        
        let pathname = '/buscar/autos/usados'; // Por defecto
        
        if (isComercial) {
            pathname = isNew ? '/buscar/comerciales/nuevos' : '/buscar/comerciales/usados';
        } else {
            pathname = isNew ? '/buscar/autos/nuevos' : '/buscar/autos/usados';
        }
        
        // Construir el state con los filtros ya normalizados
        const searchState: any = { fromChat: true };
        
        // vehicleCategory: siempre setear para que el filtro quede correcto
        if (mapped.vehicleCategory?.length) {
            searchState.vehicleCategory = mapped.vehicleCategory;
        } else {
            searchState.vehicleCategory = ['Autos']; // Default
        }
        
        if (mapped.vehicleType) searchState.vehicle_type = mapped.vehicleType;
        if (mapped.brand?.length) searchState.brand = mapped.brand.map((name: string) => ({ name }));
        if (mapped.model?.length) searchState.model = mapped.model.map((name: string) => ({ value: name, name }));
        if (mapped.year?.length) searchState.year = mapped.year;
        if (mapped.color?.length) searchState.color = mapped.color;
        if (mapped.fuel?.length) searchState.fuel = mapped.fuel;
        if (mapped.transmission?.length) searchState.transmission = mapped.transmission;
        if (mapped.type?.length) searchState.type = mapped.type;
        if (mapped.region?.length) searchState.region = mapped.region;
        if (mapped.dealership?.length) searchState.dealership = mapped.dealership;
        if (mapped.seller?.length) searchState.seller = mapped.seller;
        if (mapped.mileage?.length) searchState.mileage = mapped.mileage;
        if (mapped.price) searchState.price = mapped.price;
        
        // Guardar filtros en sessionStorage porque location.state se pierde con rutas parametrizadas
        sessionStorage.setItem('chatFilters', JSON.stringify(searchState));
        
        navigate(pathname, { state: searchState });
    };

    // Auto-aplicar filtros cuando estamos en /buscar/ y llega un NUEVO mensaje con filtros
    // Usa Set a nivel de módulo para no re-disparar al cerrar/abrir el chat
    useEffect(() => {
        const isRecentMessage = (Date.now() - message.timestamp) < 5000;
        
        if (
            isLastMessage &&
            isRecentMessage &&
            !isUser &&
            isSearch &&
            message.filters &&
            message.filtersApplied?.length &&
            location.pathname.startsWith('/buscar/') &&
            !autoTriggeredMessageIds.has(message.id)
        ) {
            autoTriggeredMessageIds.add(message.id);
            handleViewMore();
        }
    }, [isLastMessage, message.id]);

    const checkScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        checkScrollButtons();
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollButtons);
            return () => container.removeEventListener('scroll', checkScrollButtons);
        }
    }, [message.publications]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 280;
            const newScrollLeft = direction === 'left'
                ? scrollContainerRef.current.scrollLeft - scrollAmount
                : scrollContainerRef.current.scrollLeft + scrollAmount;
            
            scrollContainerRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            });
        }
    };

    const renderPublications = () => {
        if (!isSearch) return null;

        if (Array.isArray(message.publications) && message.publications.length === 0) {
            return (
                <div className={styles.noResults}>
                    No se encontraron publicaciones con esos filtros.
                </div>
            );
        }

        if (!message.publications?.length) return null;

        return (
            <div className={styles.publicationsWrapper}>
                <div className={styles.publicationsContainer}>
                    {showLeftArrow && (
                        <button
                            className={`${styles.scrollButton} ${styles.scrollButtonLeft}`}
                            onClick={() => scroll('left')}
                            aria-label="Scroll left"
                        >
                            <Icon path={mdiChevronLeft} size={1} />
                        </button>
                    )}
                    {showRightArrow && (
                        <button
                            className={`${styles.scrollButton} ${styles.scrollButtonRight}`}
                            onClick={() => scroll('right')}
                            aria-label="Scroll right"
                        >
                            <Icon path={mdiChevronRight} size={1} />
                        </button>
                    )}
                    <div className={styles.cardsGrid} ref={scrollContainerRef}>
                        {message.publications.map((pub, index) => (
                            <CardChatIA
                                key={pub._id || pub.id_publication || index}
                                item={pub}
                                navigation={`/autos/usados/${(pub.id_publication || pub._id || '').replace(/\//g, '-')}`}
                                className={styles.chatCard}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </div>
                <button
                    onClick={handleViewMore}
                    disabled={disabled}
                    className={styles.viewMoreLink}
                >
                    Ver más...
                </button>
            </div>
        );
    };

    return (
        <div className={`${styles.messageWrapper} ${isUser ? styles.userMessage : styles.assistantMessage}`}>
            {!isUser && (
                <div className={styles.assistantLabel}>Linze IA</div>
            )}
            <div className={styles.messageBubble}>
                <div
                    className={styles.messageContent}
                    dangerouslySetInnerHTML={{ __html: message.content }}
                    onClick={handleLinkClick}
                />
                {renderSearchFilters()}
                <div className={styles.messageTime}>{formatTime(message.timestamp)}</div>
            </div>
            {renderPublications()}
        </div>
    );
};

export default ChatMessage;
